// @flow
import type {Node} from 'react'
import React, {useCallback, useEffect, useState} from 'react'
import Thinking from 'components/ai/Thinking'
import Markdown from 'components/ui/Markdown'
import FrogBotIcon from 'components/ui/icons/FrogBotIcon'
import classNames from 'classnames'
import {fetchAiSuggestion} from '../AiSuggestion'

import styles from './index.scss'

type Props = {
  orgDatabaseId: string,
  promptKey: string,
  context: {
    type: string,
    id: string,
  },
  data: Object,

}

export default function ProposalSuggestion({
  orgDatabaseId,
  promptKey,
  context,
  data,
}: Props): Node {
  const [suggestion, setSuggestion] = useState('')
  const [isFetching, setIsFetching] = useState(false)
  const [hasFetched, setHasFetched] = useState(false)

  const [isOpen, setIsOpen] = useState(true)

  const {
    tensionBody,
  } = data

  const tensionPresent = tensionBody && tensionBody !== ''
  const suggestionPresent = suggestion && suggestion !== ''

  const fetchSuggestion = useCallback(async ({resuggest, retrieveOnly}) => {
    setIsFetching(true)
    setHasFetched(true)
    const orgId = orgDatabaseId
    const response = await fetchAiSuggestion({
      orgDatabaseId: orgId,
      promptKey,
      resuggest,
      context,
      data,
      retrieveOnly,
    })
    setIsFetching(false)
    setSuggestion(response.suggestion)
  }, [context, data, orgDatabaseId, promptKey])

  const onResuggest = useCallback(() => {
    if (isFetching)
      return

    fetchSuggestion({resuggest: true, retrieveOnly: false})
  }, [fetchSuggestion, isFetching])

  useEffect((() => {
    if (!hasFetched)
      fetchSuggestion({resuggest: false, retrieveOnly: true})
  }), [fetchSuggestion, hasFetched])

  if (!tensionPresent) {
    return (
      <div className={classNames(styles.offer, styles.shared)}>
        <span><FrogBotIcon /></span>
        <span><i>{I18n.t('ai.tension_required')}</i></span>
      </div>
    )
  }

  if (isFetching) {
    return (
      <div className={classNames(styles.offer, styles.shared)}>
        <span><FrogBotIcon /></span>
        <span><Thinking /></span>
      </div>
    )
  }

  if (!suggestionPresent) {
    return (
      <div className={styles.shared}>
        <button
          type="button"
          aria-label={I18n.t('ai.new_suggestion')}
          title={I18n.t('ai.new_suggestion')}
          className={styles.getSuggestionButton}
          onClick={onResuggest}
        >
          <FrogBotIcon />
          {I18n.t('ai.get_proposal_suggestion')}
        </button>
      </div>
    )
  }

  return (
    <div className={classNames(styles.tipContainer, styles.shared)}>
      <div className={styles.header}>
        <div>
          <FrogBotIcon />
          {I18n.t('ai.proposal_suggestion')}
        </div>
        <div className={styles.buttons}>
          <button
            type="button"
            aria-label={I18n.t('ai.new_suggestion')}
            title={I18n.t('ai.new_suggestion')}
            className={styles.button}
            onClick={onResuggest}
          >
            <i className="fa fa-refresh" />
          </button>

          <button
            type="button"
            className={classNames(styles.button, styles.openCloseButton)}
            onClick={() => setIsOpen(!isOpen)}
            aria-label={I18n.t('ai.cancel_suggestion')}
            title={I18n.t('ai.cancel_suggestion')}
          >
            {isOpen
              ? <i className="fa fa-angle-down" />
              : <i className="fa fa-angle-up" />
          }
          </button>
        </div>
      </div>
      {isOpen && (
        <div className={styles.suggestionContainer}>
          <Markdown source={suggestion} />
        </div>
      )}
    </div>
  )
}
